import './ProductItemListViewLayout.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { VDomToString } from '@boost-sd/core-js';
import Button from '@components/Button';
import FormatCurrency from '@components/FormatCurrency';
import useTranslation from '@hooks/useTranslation';
import type { AddToCartState } from '@providers/CartProvider';
import { createClsNameMap } from '@utils';
import classnames from '@utils/classnames';
import { useState } from 'react';

import IntegrationProductRating from '@/features/filter-product-list/product-list/product-item/IntegrationProductRating';
import ProductImage from '@/features/filter-product-list/product-list/product-item/ProductImage';
import ProductLabel from '@/features/filter-product-list/product-list/product-item/ProductLabel';
import ProductLink from '@/features/filter-product-list/product-list/product-item/ProductLink';
import TranslateWithComponent from '@/features/filter-product-list/product-list/product-item/ProductPrice/TranslateWithComponent';
import ProductSwatch from '@/features/filter-product-list/product-list/product-item/ProductSwatch';
import ProductTitle from '@/features/filter-product-list/product-list/product-item/ProductTitle';
import ProductVendor from '@/features/filter-product-list/product-list/product-item/ProductVendor';

import type { ProductItemProps } from '../types';

const clsNameMap = createClsNameMap({
  elements: {
    image: createClsNameMap(),
    'info-group': createClsNameMap(),
    info: createClsNameMap(),
    'cta-buttons': createClsNameMap(),
    price: createClsNameMap(),
    description: createClsNameMap(),
  },
})('product-item-list-view-layout');

export type ProductItemListViewLayoutProps = ProductItemProps;

export const ProductItemListViewLayout = registryComponent(
  'ProductItemListViewLayout',
  (props: ProductItemListViewLayoutProps) => {
    const { t } = useTranslation();

    const {
      variants,
      id,
      previewProductImages,
      isSale,
      isSoldOut,
      salePercent,
      saleAmount,
      title,
      vendor,
      variant_id,
      price_min,
      compare_at_price_min,
      options_with_values,
      productDetailUrl,
      tags,
      images_info,
      handle,
      original_title,
      original_images,
    } = props;

    const [addToCartState, setAddToCartState] = useState<AddToCartState>({
      isAdding: false,
      success: false,
      fail: false,
    });

    const quickViewProductData = {
      id,
      tags,
      images_info,
      title,
      vendor,
      options_with_values,
      variants,
      variant_id,
      handle,
      original_title,
      original_images,
    };

    const handleOnClickQuickViewButton = () => {
      window.dispatchEvent(
        new CustomEvent('boost-sd-open-quick-view', {
          detail: {
            productData: quickViewProductData,
            type: 'full',
          },
        })
      );
    };

    const handleATC = () => {
      if (variants?.length === 1) {
        if (addToCartState?.isAdding) return;
        setAddToCartState((prev) => ({ ...prev, isAdding: true }));

        window.dispatchEvent(
          new CustomEvent('boost-sd-add-to-cart', {
            detail: {
              variantId: variants?.[0]?.id,
              quantity: 1,
              onSuccess: () => {
                setTimeout(() => {
                  setAddToCartState((prev) => ({
                    fail: false,
                    isAdding: false,
                    success: false,
                  }));
                }, 350);
                setAddToCartState((prev) => ({
                  ...prev,
                  isAdding: false,
                  success: true,
                }));
              },
              onFail: () => {
                setTimeout(() => {
                  setAddToCartState((prev) => ({
                    fail: false,
                    isAdding: false,
                    success: false,
                  }));
                }, 350);
                setAddToCartState((prev) => ({ ...prev, fail: true, isAdding: false }));
              },
            },
          })
        );
      } else {
        window.dispatchEvent(
          new CustomEvent('boost-sd-open-quick-view', {
            detail: {
              productData: quickViewProductData,
              type: 'mini',
            },
          })
        );
      }
    };

    const renderTranslateButtonCTA = () => {
      if (isSoldOut) return 'productItem.soldoutLabel';

      return variants?.length > 1
        ? 'productItem.atcSelectOptionsLabel'
        : addToCartState?.isAdding
        ? 'productItem.atcAddingToCartBtnLabel'
        : addToCartState?.success
        ? 'productItem.atcAddedToCartBtnLabel'
        : addToCartState?.fail
        ? 'productItem.atcFailedToCartBtnLabel'
        : 'productItem.atcAvailableLabel';
    };

    const colorOption = options_with_values.find(
      (item) => (item.original_name?.toLowerCase() || item.name) === 'color'
    );

    return (
      <div
        className={clsNameMap.root()}
        id={id.toString()}
        data-product-id={variant_id?.toString() || id.toString()}
      >
        <div className={clsNameMap.elm('image')}>
          <ProductLink href={productDetailUrl} className={classnames('product-link-image')}>
            <ProductImage
              images={previewProductImages}
              altImage={title}
              elements={{
                saleLabel:
                  isSale && !isSoldOut ? (
                    <ProductLabel
                      key='sale-label'
                      labelType='sale'
                      labelText={
                        <VDomToString vDOM={<FormatCurrency value={saleAmount} />}>
                          {({ html: saleLabelString }) => (
                            <TranslateWithComponent
                              translation='productItem.productItemSale'
                              data={{
                                salePercent,
                                saleAmount: saleLabelString,
                              }}
                            />
                          )}
                        </VDomToString>
                      }
                    />
                  ) : undefined,
                soldOutLabel: isSoldOut ? (
                  <ProductLabel
                    key='sold-out-label'
                    labelType='soldout'
                    labelText={t('productItem.productItemSoldOut')}
                  />
                ) : undefined,
              }}
              grid={{
                top: {
                  direction: 'horizontal',
                  elements: {
                    left: ['saleLabel', 'soldOutLabel'],
                  },
                },
              }}
            />
          </ProductLink>
        </div>

        <div className={clsNameMap.elm('info-group')}>
          <div className={clsNameMap.elm('info')}>
            <ProductLink href={productDetailUrl}>
              <ProductTitle title={title} />
              <ProductVendor vendor={vendor} />
              <IntegrationProductRating />
              <div className={clsNameMap.elm('price')}>
                <FormatCurrency isSale={isSale} value={price_min} />
                {compare_at_price_min !== 0 && isSale && (
                  <FormatCurrency isPriceCompare value={compare_at_price_min} />
                )}
              </div>
            </ProductLink>
            <div>
              {colorOption && (
                <ProductSwatch
                  options={colorOption.values.map((value) => ({
                    value: value.origin_title || value.title,
                    label: value.title,
                    id: `product-swatch-qv-${value.title}`,
                    labelAs: 'image',
                    displayType: 'circle',
                    showOptionValueOnHovering: true,
                  }))}
                  isPopupSelectOption
                  maxItem={4}
                />
              )}
            </div>
          </div>

          <div className={clsNameMap.elm('cta-buttons')}>
            <Button
              onClick={handleATC}
              disabled={isSoldOut}
              modifiers={['primary', 'full-width', 'large']}
              className={
                t(renderTranslateButtonCTA()) === t('productItem.atcAvailableLabel')
                  ? classnames('btn-add-to-cart')
                  : ''
              }
              title={t(renderTranslateButtonCTA())}
            >
              <span>{t(renderTranslateButtonCTA())}</span>
            </Button>
            <Button
              onClick={handleOnClickQuickViewButton}
              modifiers={['secondary', 'full-width', 'large']}
              className={classnames('btn-quick-view')}
              title={t('productItem.qvBtnLabel')}
            >
              <span>{t('productItem.qvBtnLabel')}</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }
);
